import type { ChartData } from '../types';

/**
 * Combining several query results into one, in the browser.
 *
 * A composite chart (an overview row, a revenue-vs-downloads comparison, a
 * per-app comparison) fetches several source queries and merges them here —
 * never against a database. Each result is a `{columns, rows}` frame; this
 * layer only ever sees the normalised frames the sources already return, so
 * every source (insights HTTP API, Adkernel, a future MCP) stays exactly as it
 * is.
 *
 * Today the combine is a deterministic key-join (below). A custom-SQL step over
 * these frames is a planned follow-up — deliberately not here yet: it needs a
 * query engine that runs inside the same opaque-origin, no-network sandbox the
 * custom-HTML path uses, so author SQL never runs with more privilege than the
 * untrusted HTML already does. Until that lands the model carries no `sql`
 * field, so there is nothing to execute.
 */

/**
 * A full outer join of every frame on its FIRST column — the shared key (a
 * month, a country, an app). Output is the key column plus every other column
 * from every frame, in order. A column name that appears in more than one frame
 * is prefixed with the frame key (`downloads_value`) so nothing is silently
 * overwritten.
 *
 * With a single frame this is a passthrough. It is deliberately simple: "line
 * these up by their shared key" is the composite need it serves.
 */
export const defaultMerge = (named: Record<string, ChartData>): ChartData => {
  const entries = Object.entries(named).filter(([, f]) => f.columns.length > 0);
  if (entries.length === 0) return { columns: [], rows: [] };
  if (entries.length === 1) return entries[0][1];

  const keyName = entries[0][1].columns[0];

  // Which non-key column names collide across frames → those get prefixed.
  const seen = new Map<string, number>();
  for (const [, frame] of entries) {
    for (const col of frame.columns.slice(1)) seen.set(col, (seen.get(col) ?? 0) + 1);
  }

  // Output columns, and a per-frame lookup from key value → row.
  const outColumns: string[] = [keyName];
  const framePlans = entries.map(([key, frame]) => {
    const cols = frame.columns.slice(1).map((col, i) => ({
      srcIndex: i + 1,
      outName: (seen.get(col) ?? 0) > 1 ? `${key}_${col}` : col,
    }));
    for (const c of cols) outColumns.push(c.outName);
    const byKey = new Map<string, unknown[]>();
    for (const row of frame.rows) byKey.set(String(row[0] ?? ''), row);
    return { cols, byKey };
  });

  // Key values in first-seen order across all frames.
  const keyOrder: string[] = [];
  const keySeen = new Set<string>();
  for (const [, frame] of entries) {
    for (const row of frame.rows) {
      const k = String(row[0] ?? '');
      if (!keySeen.has(k)) {
        keySeen.add(k);
        keyOrder.push(k);
      }
    }
  }

  const rows = keyOrder.map((k) => {
    const out: unknown[] = [k];
    for (const plan of framePlans) {
      const srcRow = plan.byKey.get(k);
      for (const c of plan.cols) out.push(srcRow ? (srcRow[c.srcIndex] ?? null) : null);
    }
    return out;
  });

  return { columns: outColumns, rows };
};

/** Combine named frames into one. Currently the default key-join. */
export const combineFrames = (named: Record<string, ChartData>): ChartData =>
  defaultMerge(named);
